import { Button, DataTable, FAIcon, Tooltip } from '@/components'
import { PermissionsOrMeRequired } from '@/containers/permission-containers'
import { format, PERMISSIONS, useParam, usePermissionsOrMe } from '@/utils'
import { useMutation, useSuspenseQuery } from '@apollo/client'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ResultOf } from 'gql.tada'
import { useCallback, useMemo } from 'react'
import { DELETE_USER_CHILD_MUTATION } from '../mutations'
import { USER_CHILDREN_QUERY } from '../queries'
import { ChildcareDialog } from './childcare-dialog'
import { ProfileCard } from './components'

export type UserChildNode = ResultOf<
  typeof USER_CHILDREN_QUERY
>['userChildren'][number]

interface Props {
  userId: string
}

function ChildcareCardContent({ userId }: Props) {
  const translations = useTranslate({
    name: 'users.profile.childcare.table.name',
    dob: 'users.profile.childcare.table.dob',
    illness: 'users.profile.childcare.table.illness',
    soleCaregiver: 'users.profile.childcare.table.sole-caregiver',
    noData: 'users.profile.childcare.table.no-data',

    happyBirthday: 'users.profile.childcare.happy-birthday',
  })

  const hasPerms = usePermissionsOrMe({ id: userId })
  const canEdit = hasPerms(PERMISSIONS.users.change.userchildother)
  const canDelete = hasPerms(PERMISSIONS.users.delete.userchildother)

  const { data: userChildrenData } = useSuspenseQuery(USER_CHILDREN_QUERY, {
    variables: { userId },
  })

  const [deleteChild] = useMutation(DELETE_USER_CHILD_MUTATION, {
    refetchQueries: [{ query: USER_CHILDREN_QUERY, variables: { userId } }],
  })

  const handleDelete = useCallback(
    async (child: UserChildNode) => {
      await deleteChild({ variables: { id: child.id } })
    },
    [deleteChild]
  )

  const column = createColumnHelper<UserChildNode>()

  const tableColumns = useMemo(
    () => [
      column.accessor('name', {
        header: translations.name,
      }),
      column.accessor('dob', {
        header: translations.dob,
        cell(info) {
          const dob = info.getValue()
          const date = new Date(dob)
          const now = new Date()

          if (
            date.getMonth() === now.getMonth() &&
            date.getDate() === now.getDate()
          )
            return (
              <span>
                {format(date, 'PPP')}

                <Tooltip content={translations.happyBirthday}>
                  <span className="inline-block ml-2 cursor-default hover:animate-wiggle">
                    🎉
                  </span>
                </Tooltip>
              </span>
            )

          return format(date, 'PPP')
        },
      }),
      column.accessor('illness', {
        header: translations.illness,
        meta: {
          center: true,
        },
        cell(info) {
          const illness = info.getValue()

          return illness ? (
            <FAIcon className="text-green-500" icon="check" />
          ) : (
            <FAIcon className="text-red-500" icon="times" />
          )
        },
      }),
      column.accessor('soleCaregiver', {
        header: translations.soleCaregiver,
        meta: {
          center: true,
        },
        cell(info) {
          const soleCaregiver = info.getValue()

          return soleCaregiver ? (
            <FAIcon className="text-green-500" icon="check" />
          ) : (
            <FAIcon className="text-red-500" icon="times" />
          )
        },
      }),
      column.display({
        id: 'edit',
        meta: {
          center: true,
          narrow: true,
        },
        cell(info) {
          const child = info.row.original

          return (
            <div>
              {canEdit && <ChildcareDialog userId={userId} child={child} />}

              {canDelete && (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  onClick={() => handleDelete(child)}
                >
                  <FAIcon icon="trash-alt" />
                </Button>
              )}
            </div>
          )
        },
      }),
    ],
    [
      column,
      translations.name,
      translations.dob,
      translations.illness,
      translations.soleCaregiver,
      translations.happyBirthday,
      canEdit,
      userId,
      canDelete,
      handleDelete,
    ]
  )

  const tableData = useMemo(
    () => userChildrenData.userChildren ?? [],
    [userChildrenData]
  )

  return (
    <DataTable
      columns={tableColumns}
      data={tableData}
      hiddenColumns={{
        edit: !canEdit && !canDelete,
      }}
      noDataText={translations.noData}
    />
  )
}

export function ChildcareCard() {
  const translations = useTranslate({
    header: 'users.profile.childcare.header',
    description: ['users.profile.childcare.description', { link: <a /> }],
    link: 'users.profile.childcare.form.illness-read-more',
  })
  const userId = useParam('userId')

  return (
    <ProfileCard
      area="childcare"
      title={translations.header}
      description={translations.description({
        link: (
          <a
            key="illness-link"
            href="https://www.arbeidstilsynet.no/lonn-og-ansettelse/permisjoner/rett-til-permisjon-ved-barns-og-barnepassers-sykdom/"
            target="_blank"
            rel="noreferrer"
          >
            {translations.link}
          </a>
        ),
      })}
      action={
        <PermissionsOrMeRequired
          user={{ id: userId }}
          permissions={PERMISSIONS.users.add.userchildother}
        >
          <ChildcareDialog userId={userId} />
        </PermissionsOrMeRequired>
      }
      permissionsOrMe={PERMISSIONS.users.view.userchildother}
    >
      <ChildcareCardContent userId={userId} />
    </ProfileCard>
  )
}
